// Lista corta de contraseñas comunes (globales + rioplatenses) y helpers de
// normalización. La comparte el Worker (validarPassword) y el frontend
// (MedidorClave), por eso no importa nada del entorno.
//
// No pretende ser exhaustiva: la cobertura real la da Have I Been Pwned en el
// Worker. Acá van las que un mozo o cocinero uruguayo tipearía primero.

export const COMUNES: readonly string[] = [
  // numéricas / teclado
  '1234567890', '0123456789', '1234567891', '1234512345', '0987654321', '9876543210',
  '1111111111', '0000000000', '1212121212', '1231231231', '1122334455', '1234567890123',
  'qwertyuiop', 'qwerty1234', 'qwertyuiop1', 'asdfghjkl', 'asdfghjkl1', 'zxcvbnm123',
  'abcdefghij', 'abcdefghijk', 'abc1234567', 'abcd123456', 'aaaaaaaaaa', 'a1b2c3d4e5',
  // inglés (top mundiales)
  'password', 'password1', 'password12', 'password123', 'passw0rd12', 'iloveyou12',
  'sunshine12', 'princess12', 'football12', 'baseball12', 'superman12', 'welcome123',
  'letmein123', 'admin12345', 'administrator', 'trustno1', 'whatever12', 'dragon1234',
  'monkey1234', 'master1234', 'shadow1234', 'michael123', 'jennifer12', 'computer12',
  'internet12', 'starwars12', 'pokemon123', 'minecraft1', 'fortnite12', 'liverpool1',
  // español
  'contrasena', 'contrasena1', 'contrasena12', 'contrasena123', 'contraseña', 'contraseña1',
  'contraseña123', 'clave12345', 'clave123456', 'micontrasena', 'miclave123', 'secreto123',
  'teamo12345', 'tequiero12', 'teamomucho', 'mipassword', 'password12345', 'hola123456',
  'holahola12', 'holamundo1', 'bienvenido', 'bienvenido1', 'bienvenidos', 'usuario123',
  'invitado12', 'alejandro1', 'sebastian1', 'francisco1', 'fernando12', 'valentina1',
  'catalina12', 'florencia1', 'martina123', 'santiago12', 'mateo12345', 'benjamin12',
  // rioplatense
  'uruguay123', 'uruguay2024', 'uruguay2025', 'uruguay2026', 'uruguay1950', 'celeste123',
  'montevideo', 'montevideo1', 'montevideo123', 'maldonado1', 'puntadeleste', 'canelones1',
  'penarol123', 'penarol1891', 'peñarol123', 'nacional123', 'nacional1899', 'bolso12345',
  'carbonero1', 'manya12345', 'tricolor12', 'suarez1234', 'cavani1234', 'forlan1234',
  'valverde12', 'lagarto123', 'mate123456', 'asado12345', 'tortafrita', 'chivito123',
  'argentina1', 'argentina12', 'boca123456', 'bocajuniors', 'riverplate', 'river12345',
  'messi12345', 'maradona10', 'diego12345', 'gaucho1234', 'tango12345',
  // gastronómico / laboral
  'cocina1234', 'cocinero12', 'chef123456', 'mozo123456', 'salon12345', 'evento1234',
  'catering12', 'trabajo123', 'trabajo1234', 'empresa123', 'laburo1234', 'turno12345',
  'convoca123', 'convoca2026', 'convoca1234', 'admin123456', 'operador12', 'cliente123',
  'personal12', 'personal123', 'golf123456', 'clubdegolf', 'club123456',
  // fechas típicas
  '01011990', '01011995', '01012000', '10101010', '12121212', '20202020', '20242024',
  '20252025', '20262026', '19701970', '19801980', '19901990', '20002000',
];

/** Minúsculas, sin tildes ni espacios en los bordes. */
export function normalizarClave(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

const SET_COMUNES = new Set(COMUNES.map(normalizarClave));

/**
 * ¿Es una contraseña común? Compara la clave normalizada y también su "núcleo"
 * (sin dígitos/símbolos al final): "Montevideo2026!" cae por "montevideo".
 */
export function esClaveComun(password: string): boolean {
  const n = normalizarClave(password);
  if (!n) return false;
  if (SET_COMUNES.has(n)) return true;
  const nucleo = n.replace(/[\d\W_]+$/, '');
  if (nucleo.length >= 6 && nucleo !== n && SET_COMUNES.has(nucleo)) return true;
  // un solo carácter repetido o solo dos distintos ("aaaaaaaaaa", "ababababab")
  if (new Set(n).size <= 2) return true;
  return false;
}

/** Secuencias obvias de 4+ (abcd, 1234, 4321, qwer). Para el medidor. */
export function tieneSecuencia(password: string): boolean {
  const n = normalizarClave(password);
  const filas = ['abcdefghijklmnopqrstuvwxyz', '0123456789', 'qwertyuiop', 'asdfghjkl', 'zxcvbnm'];
  for (let i = 0; i + 4 <= n.length; i++) {
    const trozo = n.slice(i, i + 4);
    const reves = [...trozo].reverse().join('');
    if (filas.some((f) => f.includes(trozo) || f.includes(reves))) return true;
  }
  return /(.)\1{3,}/.test(n);
}
